import {
  HttpRequest,
  HttpEvent,
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { DriverApiTrackerService } from 'src/app/features/drivers/services/driver-api-tracker.service';

/**
 * HTTP Interceptor that tracks all POST, PUT, PATCH, DELETE API calls
 * for driver-related endpoints when enabled
 *
 * This interceptor automatically logs all data-modifying API calls
 * without requiring any changes to child components or services
 */
export const driverApiTrackingInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const apiTracker = inject(DriverApiTrackerService);

  // Check if tracking is enabled first
  if (!apiTracker.isEnabled()) {
    // Tracking disabled, just pass through
    return next(req);
  }

  // Don't track the compliance notification endpoint itself to avoid infinite loops
  if (req.url.includes('/compliance/')) {
    return next(req);
  }

  // Don't track file upload endpoint (file uploads are tracked manually where needed)
  if (req.url.includes('/provider/file/upload-file')) {
    return next(req);
  }

  // Only track POST, PUT, PATCH, DELETE methods (data-modifying operations)
  const isTrackedMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
    req.method,
  );

  if (!isTrackedMethod) {
    // For GET requests or other methods, just pass through
    return next(req);
  }

  // Check if this is a driver-related API endpoint
  const isDriverApi = true;

  if (!isDriverApi) {
    // Not a driver API, pass through without tracking
    return next(req);
  }

  // Extract information from the request
  const actionType = determineActionType(req.method, req.url);
  const driverId = extractDriverId(req.url, req.body);

  // Execute the request and track the result
  return next(req).pipe(
    tap({
      next: event => {
        // Only track when we get the final response
        if (event instanceof HttpResponse) {
          // Try to extract vehicle ID from response body for CREATE operations
          let vehicleIdFromResponse = undefined;
          if (
            req.method === 'POST' &&
            req.url.includes('/vehicles') &&
            event.body?.Data?.Id
          ) {
            vehicleIdFromResponse = Number(event.body.Data.Id);
            console.log(
              '🆔 Vehicle ID extracted from response:',
              vehicleIdFromResponse,
            );
          }

          // Create enhanced payload with response data
          const enhancedPayload = {
            ...req.body,
            _responseVehicleId: vehicleIdFromResponse,
          };

          apiTracker.trackAction(
            req.method as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
            req.url,
            actionType,
            enhancedPayload,
            driverId,
          );
        }
      },
      error: error => {
        // Track failed API calls as well
        apiTracker.trackAction(
          req.method as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
          req.url,
          actionType,
          req.body,
          driverId,
        );
      },
    }),
  );
};

/**
 * Determine the human-readable action type from method and URL
 */
function determineActionType(method: string, url: string): string {
  // Vehicle operations
  if (url.includes('/vehicles')) {
    if (method === 'POST') return 'Create Vehicle';
    if (method === 'PUT') return 'Update Vehicle';
    if (method === 'PATCH') return 'Update Vehicle Status';
    if (method === 'DELETE') return 'Delete Vehicle';
  }

  // Default fallback
  return `${method} ${url.split('/').pop() ?? 'Action'}`;
}

/**
 * Extract driver ID from URL or request body
 */
function extractDriverId(url: string, body: any): number | undefined {
  // Try to extract from URL patterns
  // Pattern: /provider/partners/{id}/...
  const partnerIdMatch = /\/provider\/partners\/(\d+)/.exec(url);
  if (partnerIdMatch) {
    return Number(partnerIdMatch[1]);
  }

  // Try to extract from request body
  if (body) {
    // Check common field names
    const idFields = ['Id', 'PartnerId', 'DriverId', 'ContractorId'];
    for (const field of idFields) {
      if (body[field]) {
        const id = Number(body[field]);
        if (!Number.isNaN(id)) return id;
      }
    }
  }

  return undefined;
}
